// Beacon pathfinding (B5, monorail-w77). The orb/home beacons used to
// point straight-line — which lies in a walled world (the arrow aims
// through three banks at an orb that's actually a long hall detour away;
// research P8: the beacon must survive walls before a map screen earns
// its place). This module turns "where is it" into "which way do I swim":
// Dijkstra over the hall graph toward the target, respecting closed
// doors, returning the next waypoint on the shortest path.
//
// Scale note: the world graph is ~45 nodes / ~65 edges. A linear-scan
// Dijkstra per beacon per frame is microseconds; no caching needed.

import type { DungeonLayout, Vec2 } from './types'

/** Node whose carved primitive is nearest to the point — same attribution
 *  rule as depth-field: discs claim their room, capsules the nearer end. */
export function nearestNodeAt(layout: DungeonLayout, x: number, y: number): number {
  let bestSurf = Infinity
  let node = layout.hubNode ?? 0
  for (const disc of layout.discs) {
    const surf = Math.hypot(x - disc.x, y - disc.y) - disc.r
    if (surf < bestSurf) {
      bestSurf = surf
      node = disc.node
    }
  }
  for (const c of layout.capsules) {
    const bax = c.bx - c.ax
    const bay = c.by - c.ay
    const len2 = bax * bax + bay * bay
    const h = len2 > 0 ? Math.max(0, Math.min(1, ((x - c.ax) * bax + (y - c.ay) * bay) / len2)) : 0
    const surf = Math.hypot(x - c.ax - bax * h, y - c.ay - bay * h) - c.r
    if (surf < bestSurf) {
      bestSurf = surf
      const e = layout.graph.edges[c.edge]
      node = h < 0.5 ? e.a : e.b
    }
  }
  return node
}

/** Distance-to-target field over passable edges (closed doors are walls,
 *  open ones are halls — a portal-opened shortcut immediately reroutes
 *  every beacon through it). */
function distancesToward(layout: DungeonLayout, target: number): number[] {
  const nodes = layout.graph.nodes
  const closed = new Set<number>()
  for (const door of layout.doors ?? []) {
    if (!door.open) closed.add(door.edge)
  }
  const adj: Array<Array<{ to: number; w: number }>> = nodes.map(() => [])
  for (let i = 0; i < layout.graph.edges.length; i++) {
    if (closed.has(i)) continue
    const e = layout.graph.edges[i]
    const w = Math.hypot(layout.pos[e.b].x - layout.pos[e.a].x, layout.pos[e.b].y - layout.pos[e.a].y)
    adj[e.a].push({ to: e.b, w })
    adj[e.b].push({ to: e.a, w })
  }
  const dist = new Array<number>(nodes.length).fill(Infinity)
  dist[target] = 0
  const done = new Array<boolean>(nodes.length).fill(false)
  for (let k = 0; k < nodes.length; k++) {
    let u = -1
    let best = Infinity
    for (let i = 0; i < nodes.length; i++) {
      if (!done[i] && dist[i] < best) {
        best = dist[i]
        u = i
      }
    }
    if (u < 0) break
    done[u] = true
    for (const { to, w } of adj[u]) {
      if (dist[u] + w < dist[to]) dist[to] = dist[u] + w
    }
  }
  return dist
}

/** Next spot to swim toward to reach (tx,ty) from (fx,fy) along the web.
 *  Returns null when a straight line is already the truth: same room, or
 *  the target is graph-unreachable (sealed vault — the arrow should still
 *  say "it's in there"). */
export function routeWaypoint(layout: DungeonLayout, fx: number, fy: number, tx: number, ty: number): Vec2 | null {
  const from = nearestNodeAt(layout, fx, fy)
  const target = nearestNodeAt(layout, tx, ty)
  if (from === target) return null
  const dist = distancesToward(layout, target)
  if (!isFinite(dist[from])) return null

  // Next hop = the shortest-path successor of this room (argmin over
  // passable neighbors of hall length + remaining distance). Aiming at a
  // neighbor's center from anywhere in the room points roughly through
  // the connecting hall — a beacon direction, not a nav-mesh.
  let bestNode = -1
  let bestCost = Infinity
  const closed = new Set<number>()
  for (const door of layout.doors ?? []) {
    if (!door.open) closed.add(door.edge)
  }
  for (let i = 0; i < layout.graph.edges.length; i++) {
    if (closed.has(i)) continue
    const e = layout.graph.edges[i]
    const n = e.a === from ? e.b : e.b === from ? e.a : -1
    if (n < 0 || !isFinite(dist[n])) continue
    const w = Math.hypot(layout.pos[e.b].x - layout.pos[e.a].x, layout.pos[e.b].y - layout.pos[e.a].y)
    if (w + dist[n] < bestCost) {
      bestCost = w + dist[n]
      bestNode = n
    }
  }
  if (bestNode < 0) return null
  if (bestNode === target) return null // next room IS the orb's room: aim true
  return { x: layout.pos[bestNode].x, y: layout.pos[bestNode].y }
}
